import { useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Inbox, Globe, Code, Shield, LogOut, ListOrdered, Presentation, Menu, X } from "lucide-react";
import clsx from "clsx";
import { getToken, getUser, isDemoWorkspace, setToken, setUser, type SessionUser } from "./api";

type NavItem = { to: string; label: string; icon: typeof LayoutDashboard; show: boolean };

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getToken();
  const user = getUser();
  const [navOpen, setNavOpen] = useState(false);
  const menuBtn = useRef<HTMLButtonElement>(null);

  // The drawer closes whenever the page changes.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setNavOpen(false); menuBtn.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [navOpen]);

  if (!token || !user) return <Navigate to="/login" replace />;

  const isSuper = user.role === "superadmin";

  // Master / super-admin can see every screen so they can audit any tenant's
  // data. Mutations that require a tenant context (upload, crawl, …) will
  // still error cleanly because the superadmin has no tenant_id of their own.
  const items: NavItem[] = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, show: true },
    { to: "/plan", label: "Conversion plan", icon: ListOrdered, show: true },
    { to: "/discovered", label: "Discovered", icon: Inbox, show: true },
    { to: "/workbench", label: "Workbench", icon: FileText, show: true },
    { to: "/sites", label: "Sites", icon: Globe, show: true },
    { to: "/installations", label: "Helper Script", icon: Code, show: !isSuper },
    { to: "/superadmin", label: "Super Admin", icon: Shield, show: isSuper },
    { to: "/demos", label: "Client demos", icon: Presentation, show: isSuper },
  ].filter((i) => i.show);

  function logout() {
    setToken(null);
    setUser(null);
    navigate("/login");
  }

  const current = items.find((i) => location.pathname.startsWith(i.to));

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      {/* Wide screens: the sidebar is always there. */}
      <aside className="hidden lg:flex w-60 shrink-0 brand-gradient text-white flex-col shadow-elevated">
        <Sidebar items={items} user={user} onLogout={logout} />
      </aside>

      {/* Narrow screens: the same sidebar as a drawer over the page. */}
      {navOpen && (
        <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setNavOpen(false)} aria-hidden="true" />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] brand-gradient text-white flex flex-col shadow-elevated">
            <Sidebar
              items={items} user={user} onLogout={logout}
              onClose={() => { setNavOpen(false); menuBtn.current?.focus(); }}
            />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden h-14 shrink-0 flex items-center gap-3 px-3 sm:px-4 bg-white border-b border-slate-200">
          <button
            ref={menuBtn}
            type="button"
            onClick={() => setNavOpen(true)}
            className="btn-ghost !px-2"
            aria-label="Open menu"
            aria-expanded={navOpen}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <img src="/logo.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
          <div className="min-w-0 text-sm">
            <span className="font-semibold text-ink">SmartComply</span>
            {current && <span className="text-slate-500 truncate"> · {current.label}</span>}
          </div>
        </header>

        {/* relative: hidden sr-only labels (position:absolute) must be placed
            inside this scroll area, not the window — else the page gets a 2nd scrollbar */}
        <main className="relative flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-[1600px] mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:p-8">
            {isDemoWorkspace() && (
              <div role="note" className="mb-6 text-sm text-brand-900 bg-brand-50 border border-brand-200 px-4 py-3 rounded">
                <strong>Demo workspace.</strong> Look around, open any document on the Review screen, correct text and approve.
                SmartRoots adds, converts and crawls documents here, so those buttons are hidden.
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function Sidebar({ items, user, onLogout, onClose }: {
  items: NavItem[];
  user: SessionUser;
  onLogout: () => void;
  onClose?: () => void;
}) {
  return (
    <>
      <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-lg bg-white/95 flex items-center justify-center shadow-sm shrink-0">
          <img src="/logo.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold tracking-tight leading-tight">SmartComply</div>
          <div className="text-[11px] text-white/70 leading-tight">by SmartRoots</div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="p-2 -m-1 rounded-md hover:bg-white/10" aria-label="Close menu">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" aria-label="Primary">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              clsx(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-white/15 text-white shadow-sm"
                  : "text-white/80 hover:bg-white/10 hover:text-white"
              )
            }
          >
            <item.icon className="h-4 w-4" aria-hidden="true" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-white/10 text-sm">
        <div className="text-white font-medium truncate" title={user.email}>{user.email}</div>
        <div className="text-xs text-white/70 capitalize mt-0.5">{user.role}</div>
        <button onClick={onLogout} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm bg-white/10 hover:bg-white/20 border border-white/10">
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sign out
        </button>
      </div>
    </>
  );
}
